import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Building2, CheckCircle2, Loader2, Mail, UserRound } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { API } from "@/contexts/AuthContext";

const TOPICS = ["Product question", "Subscription", "Enterprise", "Partnership", "Press", "Other"];
const inputClass = "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[14px] text-slate-900 outline-none transition focus:border-[#1A4FFF] focus:ring-2 focus:ring-[#1A4FFF]/15";

function Field({ label, required, children }) {
  return (
    <label className="block text-[13px] font-semibold text-slate-700">
      {label}{required && <span className="ml-0.5 text-rose-500">*</span>}
      {children}
    </label>
  );
}

export default function AboutPage() {
  const [form, setForm] = useState({ name: "", company: "", email: "", topic: TOPICS[0], message: "", consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "About Zynthoro — Founder, company and contact";
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }
    description.setAttribute("content", "Meet Zynthoro founder Ramona Vijfvinkel, learn why Zynthoro was created and contact the company directly.");
    const script = document.createElement("script");
    script.id = "about-organization-schema";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Zynthoro",
      legalName: "Casa Haya International B.V.",
      url: "https://zynthoro.ai",
      email: "info@zynthoro.ai",
      founder: { "@type": "Person", name: "Ramona Vijfvinkel", jobTitle: "Founder & CEO" },
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  const set = (key) => (event) => setForm((current) => ({
    ...current,
    [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
  }));

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => item?.msg).filter(Boolean).join(" · ")
            : "Your message could not be sent. Please try again or email info@zynthoro.ai."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="page-about">
      <Navbar />
      <main>
        <section className="zy-section bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <p className="zy-eyebrow mb-4">About Zynthoro</p>
            <h1 className="zy-h1 max-w-3xl">Building a better way to run a business.</h1>
            <p className="zy-body mt-6 max-w-3xl">
              Zynthoro brings customers, planning, projects, finance, marketing and AI assistance together in one connected platform. It was created for growing businesses that want to spend less time switching between disconnected tools and more time building their company.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/signup?trial=1" className="zy-btn-primary">Explore the 7-day demo <ArrowRight size={16} /></Link>
              <Link to="/pricing" className="zy-btn-outline">View pricing</Link>
            </div>
          </div>
        </section>

        <section className="zy-section bg-[#FAFAFB]">
          <div className="max-w-5xl mx-auto px-6 grid gap-6 md:grid-cols-2">
            <article className="rounded-2xl border border-[#e8ebf2] bg-white p-7 sm:p-8">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#202B60]/10 text-[#202B60]"><UserRound size={21} /></div>
              <p className="zy-eyebrow mt-6">Founder & CEO</p>
              <h2 className="zy-h2 mt-2">Ramona Vijfvinkel</h2>
              <p className="zy-body mt-4">
                Ramona founded Zynthoro with a clear objective: give growing businesses one practical platform for the work they currently spread across numerous separate systems. She leads Zynthoro&apos;s product direction, commercial strategy and strategic partnerships.
              </p>
            </article>
            <article className="rounded-2xl border border-[#e8ebf2] bg-white p-7 sm:p-8">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D4AF37]/15 text-[#806916]"><Building2 size={21} /></div>
              <p className="zy-eyebrow mt-6">Our approach</p>
              <h2 className="zy-h2 mt-2">One platform. Clear information. Less repeated work.</h2>
              <p className="zy-body mt-4">
                Zynthoro connects everyday business processes with four AI specialists powered by Claude and Gemini. The platform is being built around practical workflows, clear pricing and direct control over company information.
              </p>
            </article>
          </div>
          <div className="max-w-5xl mx-auto px-6 mt-6">
            <p className="rounded-xl border border-[#e8ebf2] bg-white px-5 py-4 text-[13.5px] text-slate-600">
              Zynthoro is developed and operated by <strong className="text-slate-900">Casa Haya International B.V.</strong>, registered in the Netherlands under Chamber of Commerce number <strong className="text-slate-900">99196581</strong>.
            </p>
          </div>
        </section>

        <section className="zy-section bg-white" id="contact">
          <div className="max-w-5xl mx-auto px-6 grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="zy-eyebrow mb-4">Contact</p>
              <h2 className="zy-h2">Let&apos;s talk.</h2>
              <p className="zy-body mt-4">
                Have a question about Zynthoro, our subscriptions or a possible partnership? Send us a message and we&apos;ll respond as soon as possible.
              </p>
              <a href="mailto:info@zynthoro.ai" className="mt-6 inline-flex items-center gap-2 font-semibold text-[#202B60] hover:underline">
                <Mail size={16} /> info@zynthoro.ai
              </a>
            </div>

            {sent ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8" data-testid="contact-success">
                <CheckCircle2 size={25} className="text-emerald-700" />
                <h3 className="mt-4 text-xl font-bold text-emerald-950">Thank you. Your message has been received.</h3>
                <p className="mt-2 text-[14px] text-emerald-900">We&apos;ll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-[#e8ebf2] bg-[#FAFAFB] p-6 sm:p-8 space-y-5" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name" required><input required minLength={2} maxLength={120} className={inputClass} value={form.name} onChange={set("name")} data-testid="contact-name" /></Field>
                  <Field label="Company"><input maxLength={200} className={inputClass} value={form.company} onChange={set("company")} data-testid="contact-company" /></Field>
                </div>
                <Field label="Business email" required><input required type="email" maxLength={320} className={inputClass} value={form.email} onChange={set("email")} data-testid="contact-email" /></Field>
                <Field label="What can we help you with?" required>
                  <select required className={inputClass} value={form.topic} onChange={set("topic")} data-testid="contact-topic">
                    {TOPICS.map((topic) => <option key={topic}>{topic}</option>)}
                  </select>
                </Field>
                <Field label="Message" required><textarea required minLength={10} maxLength={4000} rows={6} className={inputClass} value={form.message} onChange={set("message")} data-testid="contact-message" /></Field>
                <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
                  <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
                </div>
                <label className="flex items-start gap-2.5 text-[12.5px] text-slate-600">
                  <input required type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4" data-testid="contact-consent" />
                  <span>I agree that Zynthoro may use my details to respond to this enquiry. Read our <Link to="/legal/privacy-policy" className="font-semibold text-[#202B60] underline">Privacy Policy</Link>.</span>
                </label>
                {error && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-800" data-testid="contact-error">{error}</p>}
                <button type="submit" disabled={busy} className="zy-btn-primary disabled:cursor-not-allowed disabled:opacity-60" data-testid="contact-submit">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Send message
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

